import React, { useState, useEffect, useRef } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { API_URL } from '../utils/api';
import { parisTodayKey, addDaysKey, keyToDate } from '../utils/parisTime';
import { useStore } from '../context/StoreContext';
import { IconPlus, IconCheck, IconCart } from '../components/icons';
import WelcomeScreen from '../components/WelcomeScreen';
import './MenuPage.css';

const CATEGORY_ORDER = ['Bouchées', 'Burgers', 'Plats', 'A partager', 'Enfants', 'Boissons'];

// Emoji projeté vers le panier selon la catégorie (le petit détail qui fait plaisir)
const FLYING_EMOJI = {
  'Bouchées': '🧀', 'Burgers': '🍔', 'Plats': '🍟', 'A partager': '🍗',
  'Enfants': '🧒', 'Boissons': '🥤', 'Desserts': '🍨'
};

function MenuPage() {
  const { addToCart, cartCount } = useStore();
  const { openCart } = useOutletContext();
  const [products, setProducts] = useState(null);
  const [backendError, setBackendError] = useState(false);
  const [showWelcome, setShowWelcome] = useState(() => !sessionStorage.getItem('hasSeenWelcome'));
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [addedId, setAddedId] = useState(null);
  const [flyingItems, setFlyingItems] = useState([]);
  const [openingStatus, setOpeningStatus] = useState({ isOpen: false, message: '', loading: true });

  const categoryRefs = useRef({});
  const isScrolling = useRef(false);

  useEffect(() => {
    (async () => {
      try {
        const response = await fetch(`${API_URL}/products`);
        if (!response.ok) throw new Error('Erreur réseau');
        setProducts(await response.json());
        setBackendError(false);
      } catch (error) {
        console.error('Erreur produits:', error);
        setProducts([]);
        setBackendError(true);
      }
    })();
  }, []);

  // Statut d'ouverture (aujourd'hui, sinon prochaine ouverture)
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        // Tout en heure de Paris (le fuseau du restaurant), pas celui du navigateur
        const currentTime = new Intl.DateTimeFormat('en-GB', {
          timeZone: 'Europe/Paris', hour: '2-digit', minute: '2-digit', hour12: false
        }).format(new Date());
        const today = parisTodayKey();
        const res = await fetch(`${API_URL}/available-slots?date=${today}`);
        if (!res.ok) throw new Error();
        const slots = await res.json();

        if (slots.length > 0) {
          const first = slots[0];
          const last = slots[slots.length - 1];
          if (currentTime >= first && currentTime <= last) {
            setOpeningStatus({ isOpen: true, message: 'Ouvert — commandez pour aujourd\'hui', loading: false });
            return;
          }
          if (currentTime < first) {
            setOpeningStatus({ isOpen: false, message: `Ouverture aujourd'hui à ${first}`, loading: false });
            return;
          }
        }

        const dayNames = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
        for (let i = 1; i <= 7; i++) {
          const dateStr = addDaysKey(today, i);
          const r = await fetch(`${API_URL}/available-slots?date=${dateStr}`);
          if (!r.ok) continue;
          const s = await r.json();
          if (s.length > 0) {
            const dayName = i === 1 ? 'demain' : dayNames[keyToDate(dateStr).getDay()];
            setOpeningStatus({ isOpen: false, message: `Ouverture ${dayName} à ${s[0]}`, loading: false });
            return;
          }
        }
        setOpeningStatus({ isOpen: false, message: 'Horaires à venir', loading: false });
      } catch {
        setOpeningStatus({ isOpen: false, message: '', loading: false });
      }
    };

    fetchStatus();
    const interval = setInterval(fetchStatus, 60000);
    return () => clearInterval(interval);
  }, []);

  // Bloquer le scroll tant que l'écran d'accueil est affiché
  useEffect(() => {
    document.body.style.overflow = showWelcome ? 'hidden' : 'unset';
    return () => { document.body.style.overflow = 'unset'; };
  }, [showWelcome]);

  // Surligner la catégorie visible au scroll
  useEffect(() => {
    const handleScroll = () => {
      if (isScrolling.current) return;
      const pos = window.scrollY + 200;
      Object.entries(categoryRefs.current).forEach(([category, ref]) => {
        if (ref && pos >= ref.offsetTop && pos < ref.offsetTop + ref.offsetHeight) {
          setSelectedCategory(category);
        }
      });
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Ajout au panier avec l'animation du produit qui vole vers le panier
  const handleAdd = (product, event) => {
    const button = event.currentTarget;
    const rect = button.getBoundingClientRect();

    // Cible : le panier flottant (mobile) s'il est visible, sinon le panier de la nav
    const floating = document.querySelector('.panier-flottant');
    const navCart = document.querySelector('.nav-panier');
    const target = (floating && floating.offsetParent !== null) ? floating : navCart;
    const targetRect = target ? target.getBoundingClientRect() : null;

    const flyingItem = {
      id: Date.now() + Math.random(),
      emoji: FLYING_EMOJI[product.category] || '🍔',
      startX: rect.left + rect.width / 2,
      startY: rect.top + rect.height / 2,
      endX: targetRect ? targetRect.left + targetRect.width / 2 : window.innerWidth - 60,
      endY: targetRect ? targetRect.top + targetRect.height / 2 : 30
    };

    setFlyingItems(prev => [...prev, flyingItem]);
    setTimeout(() => {
      setFlyingItems(prev => prev.filter(item => item.id !== flyingItem.id));
    }, 950);

    addToCart(product);
    setAddedId(product.id);
    setTimeout(() => setAddedId(null), 800);
  };

  // La carte en ligne ne montre que l'à-emporter : les produits « sur place
  // uniquement » (boissons du bar…) vivent sur la carte des tables QR.
  const productsByCategory = (products || [])
    .filter(p => p.can_take_away !== false)
    .reduce((acc, p) => {
      (acc[p.category] = acc[p.category] || []).push(p);
      return acc;
    }, {});

  Object.values(productsByCategory).forEach(list =>
    list.sort((a, b) => {
      const rank = (p) => (p.available ? 1 : 2);
      return rank(a) - rank(b);
    })
  );

  const categories = [
    ...CATEGORY_ORDER.filter(c => productsByCategory[c]),
    ...Object.keys(productsByCategory).filter(c => !CATEGORY_ORDER.includes(c))
  ];

  const scrollToCategory = (category) => {
    isScrolling.current = true;
    setSelectedCategory(category);
    const el = categoryRefs.current[category];
    if (el) window.scrollTo({ top: el.offsetTop - 160, behavior: 'smooth' });
    setTimeout(() => { isScrolling.current = false; }, 900);
  };

  return (
    <div className="menu-page">
      {showWelcome && (
        <WelcomeScreen
          openingStatus={openingStatus}
          onDiscover={() => {
            setShowWelcome(false);
            sessionStorage.setItem('hasSeenWelcome', 'true');
          }}
        />
      )}

      {/* ---- Hero ---- */}
      <section className="hero">
        <div
          className="hero-fond"
          style={{ backgroundImage: 'url(/banner-01.jpg)' }}
        />
        <div className="hero-voile" />
        <div className="hero-contenu">
          <img src="/logo.svg" alt="Le Fief" className="hero-logo" />
          <div className="hero-ornement">
            <span className="hero-ornement-ligne" />
            <span className="hero-ornement-etoile">✦</span>
            <span className="hero-ornement-ligne" />
          </div>
          <p className="hero-tagline">American Food & Bar — Lacaze, Tarn</p>

          {!openingStatus.loading && openingStatus.message && (
            <div className={`hero-statut ${openingStatus.isOpen ? 'ouvert' : 'ferme'}`}>
              <span className="hero-statut-dot" />
              {openingStatus.message}
            </div>
          )}

          <div className="hero-actions">
            <button
              className="btn btn-or btn-lg"
              onClick={() => scrollToCategory(categories[0])}
              disabled={!categories.length}
            >
              Commander en ligne
            </button>
            <Link to="/reserver" className="btn btn-ghost-blanc btn-lg">
              Réserver une table
            </Link>
          </div>
        </div>
      </section>

      {backendError && (
        <div className="container-page" style={{ marginTop: 20 }}>
          <div className="msg-erreur">
            Impossible de charger la carte pour le moment. Réessayez dans un instant
            ou appelez-nous au 07 49 11 01 86.
          </div>
        </div>
      )}

      {/* ---- Barre de catégories ---- */}
      {categories.length > 0 && (
        <nav className="categories-bar">
          <div className="categories-bar-inner">
            {categories.map(cat => (
              <button
                key={cat}
                className={`categorie-btn ${selectedCategory === cat ? 'active' : ''}`}
                onClick={() => scrollToCategory(cat)}
              >
                {cat}
              </button>
            ))}
          </div>
        </nav>
      )}

      {/* ---- La carte ---- */}
      <div className="container-page">
        {products === null && (
          <div className="menu-skeletons">
            {[...Array(6)].map((_, i) => <div key={i} className="skeleton menu-skeleton" />)}
          </div>
        )}

        {categories.map(category => (
          <section
            key={category}
            className="categorie-section"
            ref={(el) => (categoryRefs.current[category] = el)}
          >
            <h2 className="section-title">{category}</h2>
            <div className="produits-grille">
              {productsByCategory[category].map(product => {
                const rupture = !product.available;
                const desactive = rupture;
                const added = addedId === product.id;

                return (
                  <article
                    key={product.id}
                    className={`produit-carte ${desactive ? 'desactive' : ''}`}
                  >
                    <div className="produit-corps">
                      <div className="produit-ligne-titre">
                        <h3 className="produit-nom">{product.name}</h3>
                        <span className="produit-pointille" />
                        <span className="produit-prix">{parseFloat(product.price).toFixed(2)}€</span>
                      </div>
                      {product.description && (
                        <p className="produit-desc">{product.description}</p>
                      )}
                      {product.points_price && !desactive && (
                        <span className="produit-points-hint">★ ou {product.points_price} pts fidélité</span>
                      )}
                    </div>

                    <div className="produit-pied">
                      <span className="produit-etat">
                        {rupture && <span className="pill pill-rouge">Rupture de stock</span>}
                      </span>
                      {!desactive && (
                        <button
                          className={`produit-add ${added ? 'ajoute' : ''}`}
                          onClick={(e) => handleAdd(product, e)}
                          aria-label={`Ajouter ${product.name} au panier`}
                          title="Ajouter au panier"
                        >
                          {added ? <IconCheck size={19} /> : <IconPlus size={19} />}
                        </button>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        ))}
      </div>

      {/* ---- Rappel panier mobile ---- */}
      {cartCount > 0 && (
        <button className="panier-flottant" onClick={openCart}>
          <IconCart size={19} />
          <span>Voir mon panier</span>
          <span className="panier-flottant-count">{cartCount}</span>
        </button>
      )}

      {/* ---- Produits volants vers le panier ---- */}
      {flyingItems.map(item => (
        <div
          key={item.id}
          className="produit-volant"
          style={{
            left: `${item.startX}px`,
            top: `${item.startY}px`,
            '--vol-x': `${item.endX - item.startX}px`,
            '--vol-y': `${item.endY - item.startY}px`
          }}
        >
          {item.emoji}
        </div>
      ))}
    </div>
  );
}

export default MenuPage;
